import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * `useExpandRowEditor({ formFrom, onSave, nameOf })` — THE expand-to-edit
 * state for a simple Manage Budgets table (Crew Grades, Cost Codes, Payment
 * Types; the Ad-hoc lines until 2026-10-28). Owner, 2026-10-24: *"clicking the row makes it editable
 * without the cells changing size … clicking it should expand the row without
 * having to click the edit button"*.
 *
 * 🔀 THE LOOK is the GAG BREAKDOWN's in-place row editing since 2026-10-25
 * (`components/budget/ExpandRowParts.js` — the row's own cells become the
 * controls; extra fields and Delete open underneath). THE STATE below is
 * the Crew Members table's (`pages/BudgetCostCodes.js`
 * `handleExpandMember` / `collapseMember`), lifted out WITHOUT its week, day
 * and Gantt state — that table does not run on this hook, because its collapse
 * also clears eight day-grid states and its open fetches weeks (see the
 * caller's docblock). What is the same, and must stay the same:
 *   - SINGLE-EXPAND: one row open at a time.
 *   - The fade: `openingId` for 50ms on open, `closingId` for 250ms on close,
 *     the panel kept mounted while it fades out (`isPanelMounted`).
 *   - The open bumps a generation, so a collapse timer still pending from the
 *     previous row cannot wipe the form the new row was just seeded with (the
 *     crew R8 fix, 2026-10-19).
 *   - A dirty row ASKS (three-way prompt: Keep editing · Discard changes ·
 *     Save and close) before it collapses or before another row opens; the
 *     click is HELD and re-run with `force` on Discard, or after a save that
 *     LANDED. A failed save runs nothing (`onSave` must resolve `true` only on
 *     a `res.ok` write).
 *
 * `formFrom(row)` builds the edit form from a row — the hydration rules (`??`,
 * never `||`) belong to the caller. `onSave(id)` performs the caller's own
 * save (its payload is untouched by this hook) and resolves `true` / `false`.
 *
 * 🪤 Dirty is a per-key `String(x ?? '')` comparison against the form as
 * opened, so a stored number and the same digits typed back as a string are
 * clean. It is a UI signal, never a gate on what a save sends.
 */
const sameValue = (a, b) => String(a ?? '') === String(b ?? '');

export function formsDiffer(a, b) {
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (!sameValue(a[k], b[k])) return true;
  }
  return false;
}

export const ROW_OPEN_FADE_MS = 50;
export const ROW_CLOSE_FADE_MS = 250; // matches the panel's CSS transition

export default function useExpandRowEditor({ formFrom, onSave, nameOf }) {
  const [expandedId, setExpandedId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState(null);
  const [pending, setPending] = useState(null); // { name, what, proceed }
  const generationRef = useRef(0);
  const formFromRef = useRef(formFrom);
  const onSaveRef = useRef(onSave);
  const nameOfRef = useRef(nameOf);
  useEffect(() => {
    formFromRef.current = formFrom;
    onSaveRef.current = onSave;
    nameOfRef.current = nameOf;
  });

  // A row that is CLOSING is never dirty: collapse() leaves form/original in
  // place for the fade, and without this the chip, the dimming and the
  // unsaved prompt stayed live for ROW_CLOSE_FADE_MS after a Cancel — a click
  // on another row then prompted about edits already discarded, and "Save and
  // close" would PUT them (review M1/M2; a landed save nulls `original`).
  const isDirty = useMemo(
    () => expandedId != null && closingId !== expandedId
      && original != null && formsDiffer(form, original),
    [expandedId, closingId, form, original],
  );

  const clear = useCallback(() => {
    setExpandedId(null);
    setClosingId(null);
    setOpeningId(null);
    setForm({});
    setOriginal(null);
  }, []);

  const collapse = useCallback(() => {
    if (expandedId == null) return;
    const closing = expandedId;
    const generation = generationRef.current;
    setClosingId(closing);
    setTimeout(() => {
      if (generationRef.current !== generation) {
        setClosingId((cur) => (cur === closing ? null : cur));
        return;
      }
      clear();
    }, ROW_CLOSE_FADE_MS);
  }, [expandedId, clear]);

  const open = useCallback((row) => {
    const seeded = formFromRef.current(row);
    generationRef.current += 1;
    setForm(seeded);
    setOriginal(seeded);
    setClosingId(null);
    setOpeningId(row.id);
    setExpandedId(row.id);
    setTimeout(() => setOpeningId((cur) => (cur === row.id ? null : cur)), ROW_OPEN_FADE_MS);
  }, []);

  const toggleRef = useRef(null);
  const toggle = useCallback((row, { force = false } = {}) => {
    const name = (nameOfRef.current && nameOfRef.current(row)) || 'this row';
    const hold = (what) => setPending({
      what,
      proceed: () => toggleRef.current(row, { force: true }),
    });
    if (expandedId === row.id) {
      if (isDirty && !force) { hold(`close ${name}`); return; }
      collapse();
      return;
    }
    if (expandedId != null && isDirty && !force) { hold(`open ${name}`); return; }
    open(row);
  }, [expandedId, isDirty, collapse, open]);
  toggleRef.current = toggle;

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Save Changes: the caller's save, then (only if it landed) collapse. */
  const saveAndCollapse = useCallback(async () => {
    if (expandedId == null) return false;
    const saved = await onSaveRef.current(expandedId);
    if (saved !== true) return false;
    setOriginal(null);
    collapse();
    return true;
  }, [expandedId, collapse]);

  const keepEditing = useCallback(() => setPending(null), []);
  const discardAndContinue = useCallback(() => {
    const held = pending;
    setPending(null);
    if (held) held.proceed();
  }, [pending]);
  const saveAndContinue = useCallback(async () => {
    const held = pending;
    // The prompt closes FIRST: a save may raise its own dialog (Cost Codes'
    // spend-adjustment confirm), which must not open beneath this one.
    setPending(null);
    if (expandedId == null) return;
    const saved = await onSaveRef.current(expandedId);
    // 🔴 A save that did not land (refused, failed, or handed to another
    // dialog) DROPS the held click and leaves the row open with the edit.
    if (saved !== true) return;
    setOriginal(null);
    if (held) held.proceed();
  }, [pending, expandedId]);

  return {
    expandedId,
    form,
    setForm,
    setField,
    isDirty,
    isExpanded: (id) => expandedId === id,
    /** Open and NOT closing — the row's cells are controls only while this
     *  holds, so a Cancel turns them back into text at once (the fade keeps
     *  `isExpanded` true for ROW_CLOSE_FADE_MS). */
    isEditing: (id) => expandedId === id && closingId !== id,
    isClosing: (id) => closingId === id,
    isPanelMounted: (id) => expandedId === id || closingId === id,
    isFading: (id) => closingId === id || openingId === id,
    /** The dirty-guard dim: `opacity-40` on every OTHER row while one is dirty. */
    isDimmed: (id) => isDirty && expandedId != null && expandedId !== id,
    toggle,
    collapse,
    /** Immediate clear, no prompt — the section itself is being closed. */
    reset: clear,
    saveAndCollapse,
    pending,
    keepEditing,
    discardAndContinue,
    saveAndContinue,
  };
}
